// AdminLedgerPanel — token-ledger viewer + spend breakdown.
//
// Routes used:
//   GET /api/admin/ledger?userId=&operation=&limit=
//   GET /api/admin/ledger/breakdown?userId=

import React, { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, DollarSign, Coins, Activity } from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";

export default function AdminLedgerPanel() {
  const [rows, setRows]            = useState([]);
  const [breakdown, setBreakdown]  = useState(null);
  const [loading, setLoading]      = useState(true);
  const [userFilter, setUserFilter] = useState("");
  const [opFilter, setOpFilter]     = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (userFilter) params.set("userId", userFilter);
      if (opFilter)   params.set("operation", opFilter);
      params.set("limit", "200");

      const [r1, r2] = await Promise.all([
        api.get(`/admin/ledger?${params.toString()}`),
        api.get(`/admin/ledger/breakdown${userFilter ? `?userId=${userFilter}` : ""}`),
      ]);
      setRows(r1.data || []);
      setBreakdown(r2.data || null);
    } catch (e) {
      toast.error("Failed to load ledger");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const totals = useMemo(() => {
    let usd = 0, tokens = 0;
    for (const r of rows) { usd += r.rawUsdCost || 0; tokens += r.tokensBilled || 0; }
    return { usd, tokens, count: rows.length };
  }, [rows]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Token Ledger</h2>
          <p className="text-sm text-slate-500">Every AI call recorded. USD = raw provider cost, tokens = wallet deduction at current rate.</p>
        </div>
        <button onClick={load} className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900 flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Reload
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input
          value={userFilter} onChange={(e) => setUserFilter(e.target.value)}
          placeholder="Filter by userId (ObjectId)"
          className="p-2 border border-slate-300 rounded-md font-mono text-xs"
        />
        <input
          value={opFilter} onChange={(e) => setOpFilter(e.target.value)}
          placeholder="Filter by operation (e.g. pre_screening_tts)"
          className="p-2 border border-slate-300 rounded-md text-sm"
        />
        <button onClick={load} className="bg-orange-500 text-white rounded-md hover:bg-orange-600 text-sm font-medium">Apply</button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPI icon={Activity} label="Calls in view" value={totals.count} />
        <KPI icon={DollarSign} label="Raw USD spent" value={`$${totals.usd.toFixed(4)}`} />
        <KPI icon={Coins} label="Wallet tokens deducted" value={totals.tokens.toLocaleString()} />
      </div>

      {/* Breakdown */}
      {breakdown && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <BreakdownCard title="By operation" rows={breakdown.byOp} valueKey="usd" labelKey="_id" suffix="USD" />
          <BreakdownCard title="By user"      rows={breakdown.byUser} valueKey="usd" labelKey="_id" suffix="USD" />
          <BreakdownCard title="By day"       rows={breakdown.byDay}  valueKey="usd" labelKey="_id" suffix="USD" />
        </div>
      )}

      {/* Ledger table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading && <div className="p-8 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}

        {!loading && rows.length === 0 && (
          <div className="p-10 text-center text-slate-500 text-sm">No ledger entries match.</div>
        )}

        {!loading && rows.length > 0 && (
          <table className="w-full">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-3 py-2">Operation</th>
                <th className="text-left px-3 py-2">Model</th>
                <th className="text-right px-3 py-2">USD</th>
                <th className="text-right px-3 py-2">Tokens</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r._id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs text-slate-500">{new Date(r.ts).toLocaleString()}</td>
                  <td className="px-3 py-2 font-mono text-xs">{String(r.userId).slice(-6)}</td>
                  <td className="px-3 py-2 text-sm">{r.operation}</td>
                  <td className="px-3 py-2 text-xs">{r.modelUsage?.model || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">${(r.rawUsdCost || 0).toFixed(5)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{r.tokensBilled || 0}</td>
                  <td className={`px-3 py-2 text-xs ${
                    r.status === "ok" ? "text-emerald-600" :
                    r.status === "blocked" ? "text-rose-600" : "text-amber-600"
                  }`}>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-xl font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function BreakdownCard({ title, rows, valueKey, labelKey, suffix }) {
  const list = Array.isArray(rows) ? rows.slice(0, 8) : [];
  const max  = list.reduce((m, r) => Math.max(m, Math.abs(r[valueKey] || 0)), 0) || 1;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <h3 className="text-xs uppercase font-semibold text-slate-600 mb-3">{title}</h3>
      {list.length === 0 && <div className="text-xs text-slate-400">No data</div>}
      <ul className="space-y-2">
        {list.map((r, i) => (
          <li key={i}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-700 truncate max-w-[60%]">{String(r[labelKey] || "—").slice(0, 28)}</span>
              <span className="font-mono text-slate-500">${(r[valueKey] || 0).toFixed(4)}</span>
            </div>
            <div className="w-full h-1 bg-slate-100 rounded">
              <div className="h-1 bg-orange-400 rounded" style={{ width: `${(Math.abs(r[valueKey] || 0) / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
